import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useApp } from "../../context/AppContext";
import { photoObjectUrl } from "../../crypto/bundleCrypto";
import { buildMapPins } from "../../family/mapPins";
import { computeTreeLayout } from "../../family/treeLayout";
import type { MapPin } from "../../models/types";
import { famFocusTitle } from "../../utils/personDisplay";
import { Avatar } from "../shared/Avatar";
import { EmptyState } from "../shared/LoadingState";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function pinInitial(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return "?";
  // Prefer first letter of a family name before " family"
  const familyMatch = trimmed.match(/^(.+?)\s+family$/i);
  if (familyMatch?.[1]) return familyMatch[1][0]!.toUpperCase();
  return trimmed[0]!.toUpperCase();
}

export function FamilyMapView() {
  const { bundle, newsFeed, session, setSelectedPersonId, person } = useApp();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const personRef = useRef(person);
  const [picker, setPicker] = useState<MapPin | null>(null);

  personRef.current = person;

  const overrides = useMemo(
    () =>
      new Map(
        (newsFeed?.location_overrides ?? []).map((o) => [
          o.person_id,
          { city: o.location_city, state: o.location_state },
        ]),
      ),
    [newsFeed],
  );

  const pins = useMemo(() => {
    if (!bundle) return [];
    return buildMapPins(bundle.people, bundle.map_pins ?? [], overrides);
  }, [bundle, overrides]);

  useEffect(() => {
    if (!containerRef.current || !bundle || !session) return;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const activeSession = session;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution: "© OpenStreetMap",
          },
        },
        layers: [{ id: "osm", type: "raster", source: "osm" }],
      },
      center: [-98, 39],
      zoom: 3.2,
    });
    mapRef.current = map;

    const markers: maplibregl.Marker[] = [];
    const bounds = new maplibregl.LngLatBounds();

    const addPinMarker = (pin: MapPin) => {
      bounds.extend([pin.location_lng, pin.location_lat]);
      const el = document.createElement("div");
      el.className = "map-pin-host";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `map-pin-photo${pin.person_ids.length > 1 ? " is-group" : ""}`;
      const safeLabel = escapeHtml(pin.label);
      const initialHtml = `<span class="map-pin-initial">${escapeHtml(pinInitial(pin.label))}</span><span class="map-pin-label">${safeLabel}</span>`;
      btn.innerHTML = initialHtml;
      btn.onclick = () => {
        if (pin.person_ids.length === 1) {
          setSelectedPersonId(pin.person_ids[0]!);
        } else {
          setPicker(pin);
        }
      };
      el.appendChild(btn);
      markers.push(
        new maplibregl.Marker({ element: el, anchor: "bottom" })
          .setLngLat([pin.location_lng, pin.location_lat])
          .addTo(map),
      );

      const renderFace = (faceHtml: string) => {
        if (!btn.isConnected) return;
        btn.innerHTML = `${faceHtml}<span class="map-pin-label">${safeLabel}</span>`;
      };

      // Mirror iOS SundiusGroupAvatarView: group photo → solo face → stacked member faces.
      void (async () => {
        if (!activeSession.bundle_key_b64) return;
        const key = activeSession.bundle_key_b64;

        if (pin.photo) {
          const url = await photoObjectUrl(activeSession, key, pin.photo);
          if (url) {
            renderFace(`<img class="map-pin-img" src="${url}" alt="" width="36" height="36" />`);
            return;
          }
        }

        const memberStems = pin.person_ids
          .map((id) => personRef.current(id)?.photo)
          .filter((stem): stem is string => Boolean(stem))
          .slice(0, 4);

        if (memberStems.length === 0) return;

        if (memberStems.length === 1 || pin.person_ids.length === 1) {
          const url = await photoObjectUrl(activeSession, key, memberStems[0]!);
          if (url) {
            renderFace(`<img class="map-pin-img" src="${url}" alt="" width="36" height="36" />`);
          }
          return;
        }

        const urls = (
          await Promise.all(memberStems.map((stem) => photoObjectUrl(activeSession, key, stem)))
        ).filter((url): url is string => Boolean(url));

        if (urls.length === 0) return;
        if (urls.length === 1) {
          renderFace(`<img class="map-pin-img" src="${urls[0]}" alt="" width="36" height="36" />`);
          return;
        }

        const stack = urls
          .map(
            (url) =>
              `<img class="map-pin-img map-pin-stack-img" src="${url}" alt="" width="28" height="28" />`,
          )
          .join("");
        renderFace(`<span class="map-pin-stack">${stack}</span>`);
      })();
    };

    for (const pin of pins) {
      addPinMarker(pin);
    }

    const fitToPins = () => {
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 48, maxZoom: 9, duration: 0 });
      }
    };
    if (map.loaded()) {
      fitToPins();
    } else {
      map.once("load", fitToPins);
    }

    return () => {
      for (const m of markers) m.remove();
      map.remove();
      mapRef.current = null;
    };
    // Omit `person` — use personRef so map isn't rebuilt every render.
  }, [bundle, pins, session, setSelectedPersonId]);

  return (
    <>
      <div ref={containerRef} className="map-container" />
      {picker ? (
        <div className="overlay" onClick={() => setPicker(null)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-header">
              <button
                type="button"
                className="sheet-close"
                aria-label="Close"
                onClick={() => setPicker(null)}
              >
                ✕
              </button>
            </div>
            <div className="sheet-body stack">
              <h2 className="title-serif" style={{ margin: 0, textAlign: "center" }}>
                {picker.label}
              </h2>
              <div className="grid-faces">
                {picker.person_ids.map((id) => {
                  const p = person(id);
                  if (!p) return null;
                  return (
                    <button
                      key={id}
                      type="button"
                      className="face-card"
                      onClick={() => {
                        setPicker(null);
                        setSelectedPersonId(id);
                      }}
                    >
                      <Avatar person={p} size={64} ring />
                      <div className="name">{p.display_name}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export function FamilyTreeView() {
  const { bundle, setSelectedPersonId, person } = useApp();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 24, y: 24 });
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const fittedRef = useRef(false);

  const layout = useMemo(
    () => (bundle ? computeTreeLayout(bundle.people, bundle.meta.sibling_order) : null),
    [bundle],
  );

  useEffect(() => {
    fittedRef.current = false;
  }, [layout]);

  useEffect(() => {
    if (!canvasRef.current || !layout) return;

    const fit = (width: number, height: number) => {
      if (width < 48 || height < 48 || fittedRef.current) return;
      const fitScale = Math.min(
        1,
        (width - 48) / layout.width,
        (height - 48) / layout.height,
      );
      if (!Number.isFinite(fitScale) || fitScale <= 0) return;
      const scaledW = layout.width * fitScale;
      const scaledH = layout.height * fitScale;
      setScale(fitScale);
      setOffset({
        x: Math.max(24, (width - scaledW) / 2),
        y: Math.max(24, (height - scaledH) / 2),
      });
      fittedRef.current = true;
    };

    const el = canvasRef.current;
    fit(el.clientWidth, el.clientHeight);

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (!fittedRef.current) {
        fit(width, height);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [layout]);

  if (!bundle || !layout) return null;

  if (layout.nodes.length === 0) {
    return <EmptyState message="Family tree isn’t ready yet — try Directory, or reopen the Family tab." />;
  }

  return (
    <div
      ref={canvasRef}
      className="tree-canvas"
      onWheel={(e) => {
        e.preventDefault();
        setScale((s) => Math.min(2.5, Math.max(0.35, s - e.deltaY * 0.001)));
      }}
      onPointerDown={(e) => {
        dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!dragRef.current) return;
        setOffset({
          x: dragRef.current.ox + (e.clientX - dragRef.current.x),
          y: dragRef.current.oy + (e.clientY - dragRef.current.y),
        });
      }}
      onPointerUp={() => {
        dragRef.current = null;
      }}
    >
      <div
        className="tree-inner"
        style={{
          width: layout.width,
          height: layout.height,
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          transformOrigin: "0 0",
        }}
      >
        <svg width={layout.width} height={layout.height} className="tree-edges">
          {layout.edges.map((edge) => {
            const a = layout.nodes.find((n) => n.personId === edge.from);
            const b = layout.nodes.find((n) => n.personId === edge.to);
            if (!a || !b) return null;
            return (
              <line
                key={`${edge.from}-${edge.to}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke="var(--line)"
                strokeWidth={2}
              />
            );
          })}
        </svg>
        {layout.nodes.map((node) => {
          const p = person(node.personId);
          if (!p) return null;
          const half = node.diameter / 2;
          return (
            <button
              key={node.personId}
              type="button"
              className="tree-node"
              style={{
                left: node.x - half,
                top: node.y - half,
                width: node.diameter,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPersonId(node.personId);
              }}
            >
              <Avatar person={p} size={node.diameter} ring />
              <span className="tree-node-label">{node.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function FamilyDirectoryView() {
  const { bundle, pushPerson } = useApp();
  const [query, setQuery] = useState("");
  const [branchFilter, setBranchFilter] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  if (!bundle) return null;

  const branches = [...new Set(bundle.people.map((p) => p.branch_id).filter(Boolean))] as string[];
  const states = [...new Set(bundle.people.map((p) => p.location_state).filter(Boolean))] as string[];

  const filtered = bundle.people
    .filter((p) => p.generation >= 1 && !p.is_expected)
    .filter((p) => (branchFilter ? p.branch_id === branchFilter : true))
    .filter((p) => (stateFilter ? p.location_state === stateFilter : true))
    .filter((p) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return (
        p.display_name.toLowerCase().includes(q) ||
        (p.legal_name ?? "").toLowerCase().includes(q) ||
        (p.location_state ?? "").toLowerCase().includes(q) ||
        (p.branch_id ?? "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => a.display_name.localeCompare(b.display_name));

  return (
    <div className="stack">
      <input
        className="field"
        placeholder="Search names, state, branch…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="chip-row" role="listbox" aria-label="Family branches">
        <button type="button" className={`chip ${branchFilter ? "" : "active"}`} onClick={() => setBranchFilter(null)}>
          All branches
        </button>
        {branches.map((b) => (
          <button
            key={b}
            type="button"
            className={`chip ${branchFilter === b ? "active" : ""}`}
            onClick={() => setBranchFilter(branchFilter === b ? null : b)}
          >
            {famFocusTitle(b)}
          </button>
        ))}
      </div>
      <div className="chip-row" role="listbox" aria-label="States">
        <button type="button" className={`chip ${stateFilter ? "" : "active"}`} onClick={() => setStateFilter(null)}>
          All states
        </button>
        {states.sort().map((s) => (
          <button
            key={s}
            type="button"
            className={`chip ${stateFilter === s ? "active" : ""}`}
            onClick={() => setStateFilter(stateFilter === s ? null : s)}
          >
            {s}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <EmptyState message="No matches — try a different search or filter." />
      ) : (
        <div className="grid-faces">
          {filtered.map((p) => (
            <button
              key={p.person_id}
              type="button"
              className="face-card"
              onClick={() => pushPerson(p.person_id)}
            >
              <Avatar person={p} size={72} ring />
              <div className="name">{p.display_name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
